import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle2, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const QR_IMAGE = 'https://customer-assets-4nw71qhi.emergentagent.net/job_pooja-booking-8/artifacts/45d691d83fc3e6cf_IMG-20261009-WA0092.jpg';
const WHATSAPP_NUMBER = '919074305418';

const POOJA_LABELS = {
  'thilahomam': 'Thilahomam',
  'ganapathi-homam': 'Ganapathi Homam',
  'archana': 'Archana'
};

export default function PaymentPage() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/booking/${bookingId}`).then(res => {
      setBooking(res.data);
    }).catch(() => setError('Booking not found'));
  }, [bookingId]);

  const handlePaid = async () => {
    try {
      await axios.post(`${API}/booking/${bookingId}/payment`, { utr_number: 'paid_via_whatsapp' });
    } catch {}

    // Build WhatsApp message with booking details
    const isCart = booking.order_type === 'cart' && Array.isArray(booking.items);
    let msg = `*Booking Confirmation*\n\nBooking ID: ${booking.booking_id}\n`;

    if (isCart) {
      msg += `\nPoojas:\n`;
      booking.items.forEach((item) => {
        const poojaName = POOJA_LABELS[item.pooja_type] || item.pooja_type;
        msg += `\n--- ${poojaName} ---\n`;
        msg += `Name: ${item.customer_name || item.death_person_name || item.devotee_name}\n`;
        if (item.nakshatra) msg += `Nakshatra: ${item.nakshatra}\n`;
        if (item.death_date) msg += `Death Date: ${item.death_date}\n`;
        if (item.birth_nakshatra) msg += `Birth Nakshatra: ${item.birth_nakshatra}\n`;
        msg += `Subtotal: ₹${item.subtotal}\n`;
      });
    } else {
      const poojaName = POOJA_LABELS[booking.pooja_type] || booking.pooja_type;
      msg += `Pooja: ${poojaName}\n`;
      msg += `Name: ${booking.customer_name || booking.death_person_name || booking.devotee_name}\n`;
      if (booking.nakshatra) msg += `Nakshatra: ${booking.nakshatra}\n`;
      if (booking.death_date) msg += `Death Date: ${booking.death_date}\n`;
      if (booking.birth_nakshatra) msg += `Birth Nakshatra: ${booking.birth_nakshatra}\n`;
    }

    msg += `\nWhatsApp: ${booking.whatsapp}\n`;
    msg += `*Total: ₹${booking.total}*\n`;
    msg += `\nI have completed the payment. Please confirm my booking.`;

    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`, '_blank');
    navigate(`/booking-status/${booking.booking_id}`);
  };

  if (error) return <div className="min-h-screen flex items-center justify-center"><p className="text-red-600">{error}</p></div>;
  if (!booking) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#C2410C' }}></div></div>;

  const isCart = booking.order_type === 'cart' && Array.isArray(booking.items);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAF9F6' }}>
      <div className="sticky top-0 z-50 bg-white shadow-md">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-lg font-bold" style={{ color: '#1C1917', fontFamily: '"Cormorant Garamond", serif' }}>Complete Payment</h1>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6 pb-20">
        <div className="bg-white rounded-3xl shadow-2xl p-6 sm:p-8 space-y-6" data-testid="payment-page">
          {/* Booking Summary */}
          <div className="rounded-2xl p-5" style={{ backgroundColor: '#F5F5F4' }}>
            <h3 className="text-sm font-semibold uppercase tracking-widest mb-3" style={{ color: '#57534E' }}>Booking Summary</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span style={{ color: '#78716C' }}>Booking ID</span><span className="font-semibold" style={{ color: '#1C1917' }}>{booking.booking_id}</span></div>

              {isCart ? (
                <>
                  {booking.items.map((item, idx) => (
                    <div key={idx} className="rounded-lg p-3 mt-2" style={{ backgroundColor: '#FAFAF9', border: '1px solid #E7E5E4' }}>
                      <div className="flex justify-between items-center mb-1">
                        <span className="font-semibold text-sm" style={{ color: '#1C1917' }}>{POOJA_LABELS[item.pooja_type] || item.pooja_type}</span>
                        <span className="font-bold text-sm" style={{ color: '#C2410C' }}>₹{item.subtotal}</span>
                      </div>
                      <div className="text-xs" style={{ color: '#78716C' }}>
                        {item.customer_name || item.death_person_name || item.devotee_name}
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                <>
                  <div className="flex justify-between"><span style={{ color: '#78716C' }}>Pooja</span><span className="font-semibold" style={{ color: '#1C1917' }}>{POOJA_LABELS[booking.pooja_type] || booking.pooja_type}</span></div>
                  <div className="flex justify-between"><span style={{ color: '#78716C' }}>Name</span><span className="font-semibold" style={{ color: '#1C1917' }}>{booking.customer_name}</span></div>
                  <div className="border-t my-2" style={{ borderColor: '#D6D3D1' }}></div>
                  <div className="flex justify-between"><span style={{ color: '#78716C' }}>Temple Fee</span><span>₹{booking.temple_fee}</span></div>
                  <div className="flex justify-between"><span style={{ color: '#78716C' }}>Convenience Fee</span><span>₹{booking.convenience_fee}</span></div>
                  {booking.delivery_charge > 0 && <div className="flex justify-between"><span style={{ color: '#78716C' }}>Delivery</span><span>₹{booking.delivery_charge}</span></div>}
                  {booking.dakshina > 0 && <div className="flex justify-between"><span style={{ color: '#78716C' }}>Dakshina</span><span>₹{booking.dakshina}</span></div>}
                </>
              )}

              <div className="border-t pt-2" style={{ borderColor: '#D6D3D1' }}>
                <div className="flex justify-between items-center">
                  <span className="text-lg font-bold" style={{ color: '#1C1917' }}>Total</span>
                  <span className="text-2xl font-bold" style={{ color: '#C2410C' }} data-testid="payment-total">₹{booking.total}</span>
                </div>
              </div>
            </div>
          </div>

          {/* QR Code */}
          <div className="text-center space-y-3">
            <h3 className="text-base font-semibold" style={{ color: '#1C1917' }}>Scan QR Code to Pay</h3>
            <div className="flex justify-center">
              <div className="rounded-xl overflow-hidden border-2 shadow-lg" style={{ borderColor: '#E7E5E4', maxWidth: '280px' }}>
                <img src={QR_IMAGE} alt="Payment QR Code" className="w-full" data-testid="payment-qr-image" />
              </div>
            </div>
            <p className="text-xs" style={{ color: '#78716C' }}>
              Open any UPI app (Google Pay, PhonePe, Paytm) and scan the QR code
            </p>
          </div>

          {/* I Have Paid Button */}
          <Button onClick={handlePaid}
            className="w-full text-lg font-semibold p-6 rounded-xl shadow-lg"
            style={{ backgroundColor: '#25D366', color: 'white' }}
            data-testid="i-have-paid-btn">
            <svg viewBox="0 0 32 32" className="w-5 h-5 mr-2" fill="currentColor"><path d="M16 0C7.164 0 0 7.164 0 16c0 2.834.745 5.496 2.047 7.807L0 32l8.358-2.19A15.93 15.93 0 0 0 16 32c8.836 0 16-7.164 16-16S24.836 0 16 0zm9.489 22.54c-.391.977-1.953 1.793-2.828 1.903-.75.094-1.723.133-2.781-.174-.641-.187-1.469-.438-2.523-.86-4.406-1.766-7.281-6.188-7.5-6.469-.219-.281-1.781-2.375-1.781-4.531 0-2.156 1.125-3.219 1.547-3.656.359-.375.984-.562 1.562-.562.187 0 .359.016.516.031.422.016.641.031.922.719.281.703.969 2.359 1.047 2.531.094.172.156.375.031.594-.125.234-.187.375-.375.594-.187.219-.375.469-.531.641-.172.156-.359.328-.156.641.203.297.906 1.5 1.953 2.438 1.344 1.188 2.469 1.563 2.828 1.734.281.141.609.109.812-.109.266-.281.594-.75.922-1.219.234-.344.531-.391.859-.25.344.125 2.172 1.031 2.547 1.219.375.187.625.281.719.438.094.156.094.906-.297 1.875z"/></svg>
            I Have Paid — Send on WhatsApp
          </Button>

          <p className="text-xs text-center" style={{ color: '#78716C' }}>
            After clicking, you'll be redirected to WhatsApp to confirm your payment with us directly.
          </p>
        </div>
      </div>
    </div>
  );
}
